import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { Cohort } from '@verantos-dev/types-cohorts';

import { COHORTS_BASE_PATH } from '../constants';
import { authFetch } from './fetchers';
import { parseOrFallback } from './parseOrFallback';
import { cohortQueryKeys } from './queryKeys';
import { cohortSchema } from './schemas';

/**
 * Tolerant zod schema for the list response. We intentionally do not
 * re-validate the heavy `userMetadata`/`pagination` envelopes — consumers
 * only ever read `data` (the cohort array).
 */
const cohortListResponseSchema = z
  .object({
    data: z.array(cohortSchema).default([]),
  })
  .passthrough();

interface UseCohortListQueryParams {
  projectId: string;
  /** Force-disable the query (default: enabled when projectId is set). */
  enabled?: boolean;
  /** When true, refetches every 5s for the live cohort-list grid. */
  shouldPoll?: boolean;
}

/**
 * Shared fetcher used by both `useCohortListQuery` and imperative
 * `queryClient.prefetchQuery` callsites (e.g. project-row priming). Lives
 * at module scope so the prefetch path and the hook path stay in lock-step.
 */
export const fetchCohortList = async (projectId: string): Promise<Cohort[]> => {
  const raw = await authFetch<unknown>(`/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}`);
  const parsed = parseOrFallback(cohortListResponseSchema, raw, 'useCohortListQuery');
  // Schema fields now cover every required `Cohort` field with tolerant
  // defaults; the cast narrows zod's inferred shape (passthrough strings,
  // tolerant booleans) to the platform-side `Cohort` enums (e.g.
  // `CohortDefinitionStatus`). Safe because the schema only constrains
  // known fields and `.passthrough()` keeps any extras on the object.
  return (parsed.data ?? []) as Cohort[];
};

/**
 * Canonical TanStack read for the project's cohort list
 * (`/cohorts/<projectId>/cohort-profile`).
 *
 * Returns the flat `Cohort[]` array — callers no longer thread the legacy
 * `{ data: { data: Cohort[] } }` envelope. The RTK `urlParams` plumbing
 * is intentionally dropped: every existing consumer passes `''` or
 * `null`, so the parameter was inert.
 *
 * Replaces the RTK `useGetCohortsQuery` endpoint from `cohortAPISlice`.
 */
export const useCohortListQuery = ({
  projectId,
  enabled,
  shouldPoll,
}: UseCohortListQueryParams) => {
  return useQuery<Cohort[]>({
    queryKey: cohortQueryKeys.list(projectId),
    queryFn: () => fetchCohortList(projectId),
    enabled: enabled !== false && !!projectId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchInterval: shouldPoll ? 5000 : false,
  });
};
